import React, { useCallback, useEffect, useState } from 'react';
import { usePage } from '../../hooks/usePage';
import {
  fraudApi,
  RiskCategory,
  RiskEvent,
  RiskSeverity,
  RiskSummary,
} from '../../api/fraud';
import { PaginatedResponse } from '../../types';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Pagination } from '../../components/ui/Pagination';
import { Drawer } from '../../components/ui/Drawer';
import { Badge } from '../../components/ui/Badge';
import { useToast } from '../../hooks/useToast';

type BadgeVariant = 'danger' | 'warning' | 'success' | 'info' | 'neutral';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const titleCase = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const severityVariant = (s: RiskSeverity): BadgeVariant =>
  s === 'critical' ? 'danger' : s === 'high' ? 'warning' : s === 'medium' ? 'info' : 'neutral';

const categoryVariant = (c: RiskCategory): BadgeVariant =>
  c === 'fraud' ? 'warning' : 'info';

const SEVERITIES: RiskSeverity[] = ['critical', 'high', 'medium', 'low'];

const selectStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--neutral-300)',
  background: 'var(--neutral-100)',
  color: 'var(--neutral-800)',
  fontSize: 13,
};

const StatCard: React.FC<{
  label: string;
  value: number | string;
  accent?: string;
}> = ({ label, value, accent }) => (
  <Card padding={16}>
    <div style={{ fontSize: 11, color: 'var(--neutral-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 700, color: accent ?? 'var(--neutral-800)', marginTop: 4 }}>
      {value}
    </div>
  </Card>
);

export const RiskEvents: React.FC = () => {
  const toast = useToast();
  const [userId, setUserId] = useState('');

  usePage('Risk & Fraud', {
    placeholder: 'Filter by user ID…',
    onSearch: (val) => setUserId(val),
  });

  const [summary, setSummary] = useState<RiskSummary | null>(null);
  const [data, setData] = useState<PaginatedResponse<RiskEvent> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState<RiskSeverity | ''>('');
  const [category, setCategory] = useState<RiskCategory | ''>('');
  const [selected, setSelected] = useState<RiskEvent | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      setSummary(await fraudApi.getSummary(7));
    } catch (err) {
      toast.error(err, 'Failed to load risk summary.');
    }
  }, [toast]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fraudApi.getEvents({
        severity: severity || undefined,
        category: category || undefined,
        userId: userId || undefined,
        page,
        limit: 25,
      });
      setData(result);
    } catch (err) {
      toast.error(err, 'Failed to load risk events.');
    } finally {
      setLoading(false);
    }
  }, [severity, category, userId, page, toast]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    setPage(1);
  }, [severity, category, userId]);

  useEffect(() => {
    const timer = setTimeout(() => void fetchEvents(), 250);
    return () => clearTimeout(timer);
  }, [fetchEvents]);

  return (
    <>
      {/* Summary — last 7 days */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <StatCard label="Total (7d)" value={summary?.total ?? '—'} />
        <StatCard label="Critical" value={summary?.bySeverity.critical ?? 0} accent="var(--danger-600)" />
        <StatCard label="High" value={summary?.bySeverity.high ?? 0} accent="var(--warning-600)" />
        <StatCard label="Security" value={summary?.byCategory.security ?? 0} accent="var(--blue-600)" />
        <StatCard label="Fraud" value={summary?.byCategory.fraud ?? 0} accent="var(--warning-600)" />
      </div>

      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
            Shadow mode — flagged signals are recorded for review, not blocked.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as RiskSeverity | '')}
              style={selectStyle}
            >
              <option value="">All severities</option>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {titleCase(s)}
                </option>
              ))}
            </select>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as RiskCategory | '')}
              style={selectStyle}
            >
              <option value="">All categories</option>
              <option value="security">Security</option>
              <option value="fraud">Fraud</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['When', 'Severity', 'Category', 'Signal', 'Score', 'Reasons', 'User'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--neutral-600)',
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          borderBottom: '1px solid var(--neutral-300)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ padding: '48px 16px', textAlign: 'center', color: 'var(--neutral-400)' }}
                      >
                        No risk events match these filters
                      </td>
                    </tr>
                  ) : (
                    data.items.map((ev, i) => (
                      <tr
                        key={ev.id}
                        onClick={() => setSelected(ev)}
                        style={{
                          background: i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--neutral-300)',
                          transition: 'background 150ms ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--blue-100)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            i % 2 === 0 ? 'var(--neutral-100)' : 'var(--neutral-200)';
                        }}
                      >
                        <td style={{ padding: '12px 16px', color: 'var(--neutral-600)', whiteSpace: 'nowrap' }}>
                          {formatDate(ev.createdAt)}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge variant={severityVariant(ev.severity)}>{titleCase(ev.severity)}</Badge>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Badge variant={categoryVariant(ev.category)}>{titleCase(ev.category)}</Badge>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--neutral-800)' }}>
                          {titleCase(ev.type)}
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--neutral-800)' }}>
                          {ev.score}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--neutral-600)', maxWidth: 320 }}>
                          {ev.reasons.join('; ')}
                        </td>
                        <td
                          style={{
                            padding: '12px 16px',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            color: 'var(--neutral-500)',
                          }}
                        >
                          {ev.userId ? `${ev.userId.slice(0, 8)}…` : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <Drawer
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? titleCase(selected.type) : ''}
        subtitle={selected ? formatDate(selected.createdAt) : ''}
        width={560}
      >
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Badge variant={severityVariant(selected.severity)}>{titleCase(selected.severity)}</Badge>
              <Badge variant={categoryVariant(selected.category)}>{titleCase(selected.category)}</Badge>
              <Badge variant="neutral">Score {selected.score}</Badge>
            </div>

            {[
              { label: 'Event ID', value: selected.id },
              { label: 'Signal', value: titleCase(selected.type) },
              { label: 'User ID', value: selected.userId ?? '—' },
              { label: 'When', value: formatDate(selected.createdAt) },
            ].map((row) => (
              <div
                key={row.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid var(--neutral-200)',
                }}
              >
                <span style={{ fontSize: 12, color: 'var(--neutral-500)', fontWeight: 500 }}>{row.label}</span>
                <span
                  style={{
                    fontSize: 13,
                    color: 'var(--neutral-800)',
                    fontFamily: row.label.includes('ID') ? 'monospace' : 'inherit',
                    wordBreak: 'break-all',
                  }}
                >
                  {row.value}
                </span>
              </div>
            ))}

            <h3
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--neutral-500)',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginTop: 20,
                marginBottom: 8,
              }}
            >
              Why it was flagged
            </h3>
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--neutral-700)', fontSize: 13, lineHeight: 1.7 }}>
              {selected.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>

            {selected.metadata && Object.keys(selected.metadata).length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--neutral-500)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginTop: 20,
                    marginBottom: 8,
                  }}
                >
                  Context
                </h3>
                <pre
                  style={{
                    fontSize: 12,
                    padding: 12,
                    background: 'var(--neutral-200)',
                    borderRadius: 'var(--radius-md)',
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    color: 'var(--neutral-700)',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(selected.metadata, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};
